import { ChevronRight } from 'lucide-react';
import { Fragment, forwardRef, type HTMLAttributes, type MouseEventHandler, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface BreadcrumbItem {
  label: ReactNode;
  href?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
}

export interface BreadcrumbProps extends HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  /** Accessible name of the <nav> (default "Breadcrumb"). */
  'aria-label'?: string;
  /**
   * Custom link renderer for router integration, e.g.
   * `renderLink={(item, props) => <Link to={item.href!} {...props} />}`.
   */
  renderLink?: (item: BreadcrumbItem, props: { className: string; children: ReactNode }) => ReactNode;
}

const LINK_CLASS = 'rounded-sm text-text-2 transition-colors hover:text-text';

/** Systems › Acme ERP › Release 4.2 UAT. The last item is the current page; chevrons mirror in RTL. */
export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(function Breadcrumb(
  { items, renderLink, className, 'aria-label': ariaLabel = 'Breadcrumb', ...rest },
  ref,
) {
  return (
    <nav ref={ref} aria-label={ariaLabel} className={cn('text-13 text-text-2', className)} {...rest}>
      <ol className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          let content: ReactNode;
          if (last) {
            content = (
              <span aria-current="page" className="font-medium text-text">
                {item.label}
              </span>
            );
          } else if (renderLink && item.href) {
            content = renderLink(item, { className: LINK_CLASS, children: item.label });
          } else if (item.href) {
            content = (
              <a href={item.href} onClick={item.onClick} className={LINK_CLASS}>
                {item.label}
              </a>
            );
          } else if (item.onClick) {
            content = (
              <button type="button" onClick={item.onClick} className={LINK_CLASS}>
                {item.label}
              </button>
            );
          } else {
            content = <span>{item.label}</span>;
          }
          return (
            <Fragment key={index}>
              <li className="inline-flex min-w-0 items-center">{content}</li>
              {last ? null : (
                <li role="presentation" aria-hidden className="inline-flex items-center text-text-3">
                  <ChevronRight className="size-3.5 rtl:-scale-x-100" strokeWidth={2} />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
});
